/**
 * Every write the admin app makes, and the shape each one has to have.
 *
 * Three rules govern this file, and none of them is negotiable:
 *
 * 1. **A medium is written through `normalizeMedium`, and only the fields the form owns.**
 *    The catalog is hand-edited, so the reader that refuses a malformed row is the same one
 *    the Studio and the store use — running it before the write means an admin cannot save
 *    something the readers would later refuse. The write is a `merge`, so a field the form
 *    does not own (`createdAt`, `createdBy`, anything a later schema adds) is left alone.
 * 2. **A global primitive is patched through `applyPrimitivePatch`.** That function owns the
 *    versioning rule — identity edits bump, axis edits and renames do not — and the caller
 *    confirms the bump *before* getting here. The admin does not get a second, looser path
 *    to the same records the Studio's authors read.
 * 3. **A global pre-render carries `primitivePath: "primitives/{id}"` and NO `requestedBy`.**
 *    That absence is the whole design (ENGINEERING §4): a global render belongs to no author,
 *    so it is fetchable by id and never listed, and the paid trigger's provenance step accepts
 *    a global path with no requester precisely because of it.
 * 4. **An issue's log is only ever appended to, and every append says who spoke.** The record
 *    is a conversation between two people; nothing here rewrites what either of them said.
 */

import {
  applyPrimitivePatch,
  approveBaseReference,
  establishmentModeFor,
  globalPrimitivePath,
  normalizePrimitive,
  parsePrimitivePath,
  resolve,
  systemNow,
  VARIANT_INSTANCES,
} from '@atlas-nook/story';
import type {
  LoadPrimitive,
  Primitive,
  PrimitiveKind,
  PrimitivePatch,
  Variant,
  VariantInstance,
} from '@atlas-nook/story';
import { normalizeMedium } from '@atlas-nook/mediums';
import type { Medium } from '@atlas-nook/mediums';
import {
  createClientStore,
  illustrationPathFor,
  photoPathFor,
} from '@atlas-nook/firestore-store/client';
import {
  arrayUnion,
  deleteField,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import type { FieldValue } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';

import { storage } from '../firebase.js';
import { globalPrimitive, issue, issues, medium, variantInstance } from './collections.js';
import { db } from './firestore.js';
import { formatIssueId, ISSUE_COUNTER_ID, issueScreenshotPath, nextIssueCount } from './issues.js';
import type { IssueArea, IssueKind, IssueStatus } from './issues.js';

// ─── the catalog ─────────────────────────────────────────────────────────────

/**
 * The fields the medium form owns.
 *
 * Everything here is written on save (with `undefined` becoming a real field delete);
 * everything *not* here survives a save untouched. `id` is the document's, the stamps are the
 * server's, and `createdBy` is written once at create.
 */
export const MEDIUM_FORM_FIELDS = [
  'name',
  'family',
  'formatLabel',
  'tagline',
  'description',
  'physicalAction',
  'storytellingQuality',
  'availability',
  'order',
  'featured',
  'ageMin',
  'ageMax',
  'youngSafe',
  'tier',
  'productionEase',
  'cutterDependency',
  'consistencyRisk',
  'refillModel',
  'narrativeStrength',
  'ruledOut',
  'ruledOutReason',
  'frameModel',
  'generationNotes',
  'composition',
  'price',
  'shipsIn',
  'checkoutUrl',
  'baseSku',
  'refillSku',
  'whatArrives',
  // The list, not the files: a photograph's bytes go to Storage the moment it is picked
  // (`uploadPhotoFile`), and the rows — order, captions, sizes, the home flags — are one
  // edit that saves with the rest of the form.
  'photos',
] as const satisfies readonly (keyof Medium)[];

type MediumFormField = (typeof MEDIUM_FORM_FIELDS)[number];

/**
 * Save a medium.
 *
 * `normalizeMedium` runs first — the same defensive reader the Studio reads through — so what
 * lands in Firestore is a record every consumer can already read. `published` and `comingSoon`
 * are **not** in the field list: they are separate, confirmed decisions (below), never a side
 * effect of editing a tagline.
 */
export async function saveMedium(draft: Medium): Promise<Medium> {
  const clean = normalizeMedium(draft);

  const update: Record<string, unknown> = { updatedAt: serverTimestamp() };
  for (const field of MEDIUM_FORM_FIELDS) {
    const value = clean[field as MediumFormField];
    update[field] = value === undefined ? deleteField() : value;
  }

  await setDoc(medium(clean.id).withConverter(null), update, { merge: true });
  return clean;
}

/** A brand-new catalog row: the slug, the four fields `normalizeMedium` insists on, defaults. */
export async function createMedium(input: {
  id: string;
  name: string;
  family: Medium['family'];
  physicalAction: string;
}): Promise<void> {
  const draft = normalizeMedium({
    id: input.id,
    name: { en: input.name },
    family: input.family,
    physicalAction: { en: input.physicalAction },
    published: false,
    availability: 'concept',
    order: 0,
  });

  const { id, createdAt, updatedAt, ...fields } = draft;
  void id;
  void createdAt;
  void updatedAt;

  await setDoc(medium(input.id).withConverter(null), {
    ...fields,
    createdBy: 'admin',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Publish, unpublish, or tease.
 *
 * Its own function because it is its own decision: the two flags are what a parent can
 * actually pick from, and the confirm that precedes this states the consequence out loud.
 */
export async function setMediumAvailabilityFlags(
  id: string,
  flags: { published?: boolean; comingSoon?: boolean },
): Promise<void> {
  await updateDoc(medium(id).withConverter(null), {
    ...flags,
    updatedAt: serverTimestamp() as FieldValue,
  });
}

/** Drag-to-reorder. One write per moved row, because `order` is a plain number per document. */
export async function writeMediumOrder(rows: { id: string; order: number }[]): Promise<void> {
  await Promise.all(
    rows.map((row) =>
      updateDoc(medium(row.id).withConverter(null), {
        order: row.order,
        updatedAt: serverTimestamp() as FieldValue,
      }),
    ),
  );
}

/**
 * Upload a catalog illustration and point the record at it.
 *
 * The path shape is `@atlas-nook/firestore-store/client`'s, shared with the Studio's read, so
 * writer and reader cannot drift. The record stores the **path**; the URL is minted per
 * session by whoever shows it.
 */
export async function uploadIllustration(mediumId: string, file: File): Promise<string> {
  const path = illustrationPathFor(mediumId, file.type);
  await uploadBytes(ref(storage(), path), file, { contentType: file.type });
  await updateDoc(medium(mediumId).withConverter(null), {
    illustrationPath: path,
    updatedAt: serverTimestamp() as FieldValue,
  });
  return path;
}

// ─── keepsake photographs (PRD 03 §5) ────────────────────────────────────────

/**
 * A key for one uploaded photograph.
 *
 * Only has to be unique inside one medium's folder, and readable in a Storage listing. The
 * clock plus a few random characters is both, and needs no round trip to find out what is
 * already there.
 */
function photoKey(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Upload one photograph's file and hand back the path.
 *
 * Storage only — the caller writes the row. A clip is uploaded the same way as a still, and
 * its poster is a second call with `kind: 'still'`, so nothing here has to know that a clip
 * comes in two files.
 */
export async function uploadPhotoFile(mediumId: string, file: File): Promise<string> {
  const path = photoPathFor(mediumId, photoKey(), file.type);
  await uploadBytes(ref(storage(), path), file, { contentType: file.type });
  return path;
}

/**
 * Forget an illustration.
 *
 * The record stops pointing at the object; the object stays. Deleting it would need a
 * Storage delete the browser has no rule for, and an admin clearing a field should not be
 * able to destroy bytes by accident.
 */
export async function clearIllustration(mediumId: string): Promise<void> {
  await updateDoc(medium(mediumId).withConverter(null), {
    illustrationPath: deleteField(),
    updatedAt: serverTimestamp() as FieldValue,
  });
}

// ─── the global library ──────────────────────────────────────────────────────

/**
 * A new curated global.
 *
 * `ownerId: 'system'` and `scope: 'global'` — a global primitive is a curator publication
 * (PRD 02 §10), owned by nobody in particular and writable only through the admin claim.
 * `archivedAt: null` is explicit and load-bearing: `where('archivedAt', '==', null)` matches
 * a field that IS null and skips one that is missing, so a global created without it would be
 * invisible on the shared shelf it was made for.
 */
export async function createGlobal(input: {
  id: string;
  kind: PrimitiveKind;
  name: { en: string; tr?: string };
}): Promise<void> {
  const draft: Primitive = {
    id: input.id,
    kind: input.kind,
    scope: 'global',
    ownerId: 'system',
    name:
      input.name.tr === undefined
        ? { en: input.name.en }
        : { en: input.name.en, tr: input.name.tr },
    axes: [],
    version: 1,
    archivedAt: null,
    createdAt: null,
    updatedAt: null,
    createdBy: 'admin',
  };

  const { id, createdAt, updatedAt, ...fields } = draft;
  void id;
  void createdAt;
  void updatedAt;

  await setDoc(globalPrimitive(input.id).withConverter(null), {
    ...fields,
    establishment: establishmentModeFor(draft),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Patch a global through the engine's versioning rule.
 *
 * The same call the Studio makes for an author's piece, against a different collection —
 * identity edits bump `version` (which re-addresses the base reference and re-opens the
 * approval), axis edits and renames do not. Returns whether the version moved so the surface
 * can say what happened.
 */
export async function patchGlobal(
  primitive: Primitive,
  patch: PrimitivePatch,
): Promise<{ bumped: boolean; version: number }> {
  // Throws `AxesTooLargeError` before the network is touched: a refused axis edit costs
  // nothing and leaves nothing half-written.
  const next = applyPrimitivePatch(primitive, patch);

  const update: Record<string, unknown> = { updatedAt: serverTimestamp() };
  for (const [key, value] of Object.entries(patch)) {
    update[key] = value === undefined ? deleteField() : value;
  }
  if (next.version !== primitive.version) update['version'] = next.version;

  await updateDoc(globalPrimitive(primitive.id).withConverter(null), update);
  return { bumped: next.version !== primitive.version, version: next.version };
}

/**
 * Take a global off the shared shelf, reversibly.
 *
 * The take-down lean (PRD 06): an archived global vanishes from Browse and keeps resolving,
 * so a story that already used it still renders. There is **no permanent delete** for a
 * global in this app — a curated publication outlives whoever filed it, and the reference
 * check that a real deletion would need is a callable scoped to an author's own library.
 */
export async function archiveGlobal(id: string): Promise<void> {
  await updateDoc(globalPrimitive(id).withConverter(null), {
    archivedAt: serverTimestamp() as FieldValue,
    archivedBy: 'admin',
    updatedAt: serverTimestamp() as FieldValue,
  });
}

export async function restoreGlobal(id: string): Promise<void> {
  await updateDoc(globalPrimitive(id).withConverter(null), {
    archivedAt: null,
    archivedBy: null,
    updatedAt: serverTimestamp() as FieldValue,
  });
}

// ─── pre-rendering a global ──────────────────────────────────────────────────

/** Reads a primitive by its path, for the engine's approval gate. Global paths only. */
const loadPrimitive: LoadPrimitive = async (primitiveId, path) => {
  if (path === undefined) return null;
  if (parsePrimitivePath(path) === null) return null;
  const snapshot = await getDoc(doc(db(), path));
  if (!snapshot.exists()) return null;
  return normalizePrimitive(primitiveId, snapshot.data());
};

export interface PreRenderRequest {
  primitive: Primitive;
  /** `{}` is the base reference — the neutral variant every other point is drawn from. */
  variant: Variant;
  themeId: string;
  /** Redraw an address that already resolved. **Costs a generation** — confirm it first. */
  force?: boolean;
}

/**
 * Ask for a global's picture — the admin-pre-render lean (PRD 06), made concrete.
 *
 * The record it writes carries `primitivePath: "primitives/{id}"` and **no `requestedBy`**,
 * which the rules allow for exactly this case and the trigger's provenance step accepts
 * without a requester. No `enqueue`: the `onVariantInstanceCreated` trigger already fires on
 * the write, so passing one would ask for the generation twice.
 *
 * `now` is a Firestore `Timestamp` rather than the engine's default clock, whose
 * `TimestampLike` carries a `toMillis()` *method* that Firestore refuses to serialize.
 */
export async function preRender(request: PreRenderRequest): Promise<VariantInstance> {
  return resolve(
    {
      store: createClientStore<VariantInstance>(db(), VARIANT_INSTANCES),
      loadPrimitive,
      now: () => Timestamp.now(),
    },
    {
      primitiveId: request.primitive.id,
      primitiveVersion: request.primitive.version,
      variant: request.variant,
      themeId: request.themeId,
      primitivePath: globalPrimitivePath(request.primitive.id),
      primitive: request.primitive,
      // No `requestedBy`. A global render belongs to nobody, and the absence is the design.
      ...(request.force === true ? { force: true } : {}),
    },
  );
}

/**
 * Approve a global character's model sheet — the same three-key write the Studio makes.
 *
 * The rules allow it on a global path (`isReadablePrimitivePath` covers the global library),
 * and the approval is what fires `onModelSheetApproved`: the free `sharp` split that cuts the
 * sheet into the starter variants and re-points `assetPath` at the canonical crop.
 *
 * Stamped with the admin's uid, because `approvedBy` must equal `request.auth.uid` — "who
 * accepted this" is a person, even when the primitive belongs to `system`.
 */
export async function approveGlobalBaseReference(
  instance: VariantInstance,
  uid: string,
): Promise<void> {
  // The engine refuses anything that is not a *ready base reference*, before the network.
  const approved = approveBaseReference(instance, { by: uid, at: systemNow() });

  await updateDoc(variantInstance(instance.id).withConverter(null), {
    approved: approved.approved ?? true,
    approvedAt: serverTimestamp() as FieldValue,
    approvedBy: uid,
  });
}

// ─── the issues tracker ──────────────────────────────────────────────────────

export interface NewIssue {
  title: string;
  area: IssueArea;
  kind: IssueKind;
  step?: string;
  note?: string;
}

/**
 * File one, and hand back its id.
 *
 * The id comes out of a transaction over `issues/_meta`, so two tabs filing at the same
 * moment cannot both be `i-0007`. The counter and the issue are written in the **same**
 * transaction: a counter that moved without an issue landing would burn an id, and an issue
 * that landed without the counter moving would collide with the next one.
 *
 * `log: []` on purpose — a "filed" line would only restate `createdAt`, which the detail page
 * already shows. The log is for what happened *after*.
 */
export async function createIssue(input: NewIssue): Promise<string> {
  const collectionRef = issues().withConverter(null);
  const meta = doc(collectionRef, ISSUE_COUNTER_ID);

  return runTransaction(db(), async (transaction) => {
    const snapshot = await transaction.get(meta);
    const count = nextIssueCount(snapshot.exists() ? snapshot.data() : null);
    const id = formatIssueId(count);

    transaction.set(meta, { count });
    transaction.set(doc(collectionRef, id), {
      title: input.title,
      area: input.area,
      kind: input.kind,
      status: 'open',
      log: [],
      ...(input.step === undefined ? {} : { step: input.step }),
      ...(input.note === undefined ? {} : { note: input.note }),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return id;
  });
}

/**
 * Say something on an issue, and optionally move it.
 *
 * One function for both because they are one act: a status change with no word on it is the
 * thing that makes a tracker useless six weeks later. `at` is a **client** `Timestamp` —
 * `serverTimestamp()` is a sentinel and Firestore refuses one inside an array element.
 *
 * `by` is `'namik'` from this app and `'agent'` from the CLI. There are two hands on this
 * collection and no assignment field; naming them is the whole of the provenance.
 */
export async function appendIssueLog(
  id: string,
  entry: { text: string; status?: IssueStatus },
): Promise<void> {
  await updateDoc(issue(id).withConverter(null), {
    ...(entry.status === undefined ? {} : { status: entry.status }),
    log: arrayUnion({ at: Timestamp.now(), by: 'namik', text: entry.text }),
    updatedAt: serverTimestamp() as FieldValue,
  });
}

/**
 * Attach screenshots to an issue.
 *
 * The bytes go to `issues/{id}/{n}.{ext}`, which the Storage rules close to everyone without
 * the admin claim — read as well as write, unlike a catalog illustration. `startIndex` keeps
 * a second upload from overwriting the first, and the record stores **paths**; the URL is
 * minted per session by whoever shows it.
 */
export async function uploadIssueScreenshots(
  id: string,
  files: File[],
  startIndex = 0,
): Promise<string[]> {
  const paths: string[] = [];
  for (const [offset, file] of files.entries()) {
    const path = issueScreenshotPath(id, startIndex + offset + 1, file.type);
    await uploadBytes(ref(storage(), path), file, { contentType: file.type });
    paths.push(path);
  }

  if (paths.length > 0) {
    await updateDoc(issue(id).withConverter(null), {
      screenshots: arrayUnion(...paths),
      updatedAt: serverTimestamp() as FieldValue,
    });
  }
  return paths;
}
